import React from 'react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import RecommendedProjectsContainer from '@jira/global-pages__home-project-recommendations/src/RecommendedProjectsContainer.tsx';
import RecommendedProjectsSection from '@jira/global-pages__home-project-recommendations/src/RecommendedProjectsSection.tsx';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	CUSTOMER_SERVICE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { useRecentBoards } from '@atlassian/jira-recents-store/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { MAX_CARDS, PROJECTS_DIRECTORY_URL } from '../common/constants.tsx';
import type { DeepDiveItem, Project } from '../common/types.tsx';
import {
	type Props as LinkWithAnalyticsProps,
	LinkWithAnalytics,
} from '../common/ui/link/index.tsx';
import { useDeepDiveCardContext as useDeepDiveCardContextDI } from '../services/deep-dive-provider/main.tsx';
import { BoardsDropdown, FilterShortcuts } from './deep-dive-card/index.tsx';
import { DeepDiveCard } from './deep-dive-card/main.tsx';
import { EmptyStateDeepDiveCard } from './empty-state/index.tsx';
import messages from './messages.tsx';

type Props = {
	useDeepDiveCardContext?: typeof useDeepDiveCardContextDI;
};

const getLandingUrl = (
	project: Project,
	boards: { data?: DeepDiveItem | undefined },
	recentData: ReturnType<typeof useRecentBoards>[0],
) => {
	const recentBoard = recentData.items?.find(
		(x) => x.attributes.containerId === String(project.id),
	);

	return project.projectType === SOFTWARE_PROJECT ||
		project.projectType === SERVICE_DESK_PROJECT ||
		project.projectType === PRODUCT_DISCOVERY_PROJECT ||
		(project.projectType === CUSTOMER_SERVICE_PROJECT && fg('csm_rename_jcs_to_csm'))
		? (recentBoard?.url ?? boards.data?.items?.at(0)?.href)
		: null;
};

export const DeepDiveCardsSectionExisting = ({
	// eslint-disable-next-line react-hooks/react-compiler
	useDeepDiveCardContext = useDeepDiveCardContextDI,
}: Props): React.JSX.Element => {
	const viewAllGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.viewAllGalaxia
		: messages.viewAll;
	// eslint-disable-next-line react-hooks/react-compiler
	const { data, sectionTitle, isloadingProjects } = useDeepDiveCardContext();
	const { formatMessage } = useIntl();
	const maxlen = MAX_CARDS;

	const projectsDirectoryUrl = '/jira/projects';

	const [recentData] = useRecentBoards();

	return (
		<Box xcss={OuterSectionStyles}>
			{isloadingProjects && <Box xcss={PlaceholderStyles} />}
			{!isloadingProjects &&
				(data.length > 0 ? (
					<Stack space="space.100">
						<Box xcss={HeaderStyles}>
							<SectionTitle>{sectionTitle}</SectionTitle>
							<ViewAllLink href={projectsDirectoryUrl} actionSubjectId="viewAllProjectsLink">
								{formatMessage(viewAllGalaxia)}
							</ViewAllLink>
						</Box>

						<Box xcss={HorizontalScrollingStyles}>
							<Box xcss={SectionStyles}>
								{data.slice(0, maxlen).map(({ project, boards, filters }, index) => {
									const globalErrorState =
										boards.error === true && filters.error === true && !filters.data;

									const card = (
										<DeepDiveCard
											project={project}
											key={index}
											landingUrl={getLandingUrl(project, boards, recentData)}
										>
											<FilterShortcuts project={project} filters={filters} />
											{globalErrorState !== true && (
												<BoardsDropdown project={project} boards={boards} />
											)}
										</DeepDiveCard>
									);
									return card;
								})}
							</Box>
						</Box>
						<Box xcss={HorizontalScrollOverflowFadeStyles} />
					</Stack>
				) : (
					<Box xcss={EmptyWrapperStyles}>
						<EmptyStateDeepDiveCard
							projectsDirectoryUrl={projectsDirectoryUrl}
							actionSubjectId="recentProjectsBrowseProjectsButton"
						/>
					</Box>
				))}
		</Box>
	);
};

// For You Project Recommendation Experiment - recommended_projects_for_you_page
export const DeepDiveCardsSectionExperiment = ({
	// eslint-disable-next-line react-hooks/react-compiler
	useDeepDiveCardContext = useDeepDiveCardContextDI,
}: Props) => {
	// eslint-disable-next-line react-hooks/react-compiler
	const { data, isloadingProjects, recommendedProjectsData } = useDeepDiveCardContext();
	const maxlen = MAX_CARDS;

	const [recentData] = useRecentBoards();

	return (
		<Box xcss={OuterSectionStyles}>
			{isloadingProjects && <Box xcss={PlaceholderStyles} />}
			{!isloadingProjects && (
				<RecommendedProjectsContainer
					projectsDirectoryUrl={PROJECTS_DIRECTORY_URL}
					recommendedProjects={recommendedProjectsData?.recommendations ?? []}
				>
					<RecommendedProjectsSection
						recommendations={recommendedProjectsData?.recommendations ?? []}
					/>
					<>
						{data.length > 0 ? (
							<Box xcss={HorizontalScrollingStyles}>
								<Box xcss={SectionStyles}>
									{data.slice(0, maxlen).map(({ project, boards, filters }, index) => {
										const globalErrorState =
											boards.error === true && filters.error === true && !filters.data;
										const card = (
											<DeepDiveCard
												project={project}
												key={index}
												landingUrl={getLandingUrl(project, boards, recentData)}
											>
												<FilterShortcuts project={project} filters={filters} />
												{globalErrorState !== true && (
													<BoardsDropdown project={project} boards={boards} />
												)}
											</DeepDiveCard>
										);
										return card;
									})}
								</Box>
							</Box>
						) : (
							<Box xcss={EmptyWrapperStyles}>
								<EmptyStateDeepDiveCard
									projectsDirectoryUrl={PROJECTS_DIRECTORY_URL}
									actionSubjectId="recentProjectsBrowseProjectsButton"
								/>
							</Box>
						)}
					</>
				</RecommendedProjectsContainer>
			)}
		</Box>
	);
};

export const DeepDiveCardsSection = componentWithCondition(
	() => expValEquals('recommended_projects_for_you_page', 'cohort', 'test', 'control'),
	DeepDiveCardsSectionExperiment,
	DeepDiveCardsSectionExisting,
);

export default DeepDiveCardsSection;

const OuterSectionStyles = xcss({
	display: 'block',
	position: 'relative',
	paddingTop: 'space.200',
	marginTop: 'space.0',
	marginBottom: 'space.300',
	borderTop: '1px solid',
	borderColor: 'color.border',
});

const HeaderStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
});

const HorizontalScrollingStyles = xcss({
	overflowX: 'auto',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	position: 'relative', // This is needed to position the Box that uses the HorizontalScrollOverflowFadeStyles
});

const SectionStyles = xcss({
	display: 'inline-flex',
	whiteSpace: 'nowrap',
});

// The purpose of these styles is to create a fade effect on the right side of the horizontal scroll to give the user
// a visual cue that there is more content to scroll to. The linear transition from transparent to white is used to
// create the fade effect.
const HorizontalScrollOverflowFadeStyles = xcss({
	display: 'block',
	position: 'absolute',
	right: 'space.0',
	top: 'space.400',
	bottom: 'space.0',
	width: token('space.400'),
	background: `linear-gradient(
        90deg,
        transparent,
${''}
        ${token('elevation.surface')} 90%
    )`,
});

const EmptyWrapperStyles = xcss({
	paddingTop: 'space.300',
	paddingBottom: 'space.400',
	paddingLeft: 'space.0',
	paddingRight: 'space.0',
});

const PlaceholderStyles = xcss({
	height: '196px',
	alignItems: 'center',
	alignContent: 'center',
	display: 'flex',
	justifyContent: 'center',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionTitle = styled.h2`
	font: ${token('font.heading.small')};
	font-weight: ${token('font.weight.medium')};
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ViewAllLink: StyledComponentClass<LinkWithAnalyticsProps, any> = styled(
	LinkWithAnalytics,
)`
	font: ${token('font.body')},
	display: inline-flex;
	text-decoration: none;
	color: ${token('color.link')};
	margin: 0;
`;
